import type { RoundEvent } from "@slot/client-engine";
import type { HoldLandingProfile, HoldMotionTuning } from "@slot/mechanics-hold";
import { BLANK } from "../rules/symbols";
import { BLANK_LIT } from "../symbols/SymbolCatalog";

// The cells' motion VALUES (the hold mechanic's shapes, decision 50) — the only file to edit to retune how the cells
// roll and land. Lane spacings are `ROLL_WINDOW.laneSpacing` (scene/layout.ts).

export const HOLD_MOTION_TUNING: HoldMotionTuning = {
  // The fillers' art is blurred along the roll (tools/art), so the lanes are not stretched as well.
  roll: { lanesPerSecond: 11, spinUpSeconds: 0.12, fillerStretchY: 1 },
  // A coin stops on its cell, no bounce. A coin is taller than its window (a 233 disc in 148): it leaves the window
  // 0.2 of a lane above its cell, where its disc reaches the window's bottom edge, so it is never cut off on its way
  // down (decision 53); then it stops and pops, unmasked. An empty cell's badge, blue or an active line's red, bounces
  // instead (it never pops): past its cell and back, as every cell did before the coins' pop (decision 70; the user,
  // 2026-10-11).
  land: {
    overshootLanes: 0,
    settleSeconds: 0,
    settleEasing: "sineInOut",
    unmaskLanes: 0.2,
    bySymbol: { [BLANK]: { overshootLanes: 0.09, settleSeconds: 0.14 }, [BLANK_LIT]: { overshootLanes: 0.09, settleSeconds: 0.14 } },
  },
  slamBrakeSeconds: 0.14,
  speedModes: { turbo: { staggerScale: 0.45, minRollScale: 0.5 }, super_turbo: { staggerScale: 0, minRollScale: 0.25 } },
};

/** A base spin lands cell by cell, row by row, left to right; a teased cell (a line's last, one symbol short) rolls on. */
export const BASE_LANDING: HoldLandingProfile = { order: "cells", minRollSeconds: 0.45, staggerSeconds: 0.08, anticipationSeconds: 1.1 };

/** A respin lands its rolling cells one at a time, in reading order. */
export const RESPIN_LANDING: HoldLandingProfile = { order: "cells", minRollSeconds: 0.5, staggerSeconds: 0.2, anticipationSeconds: 0 };

/**
 * A coin's pop as it lands (seconds of the round's runner, its swell a share of its size): it swells and settles. After
 * the player's slam (a tap, or a held Spin) a quicker, smaller one, so a slammed board still settles with a beat (the
 * user, 2026-10-10).
 */
export const COIN_LAND_POP = {
  land: { seconds: 0.24, swell: 0.12 },
  slam: { seconds: 0.12, swell: 0.06 },
} as const;

/** The profile for an event: the base (and bought) spin by columns, every respin by cells. */
export function landingProfile(event: RoundEvent): HoldLandingProfile {
  return event.eventTypeName === "spin" ? BASE_LANDING : RESPIN_LANDING;
}
